import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { StatusBadge, PageLoader, ErrorState, EmptyState, Field } from '../../components/ui';
import { eventsApi } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import RichTextEditor from '../../components/RichTextEditor';
import GalleryEditor from '../../components/GalleryEditor';
import { galleryItemsFromRecord, buildGalleryPayload, applyGalleryErrors } from '../../lib/gallery';

const EMPTY_FORM = {
  id: null,
  title: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  location: '',
  capacity: '',
  description: '',
  isFeatured: false,
  gallery: [],
};

/**
 * Build an ISO-8601 UTC string from LOCAL date/time inputs.
 * The Date constructor treats the parts as local time, so .toISOString()
 * converts to the correct UTC instant. The calendar groups by LOCAL date,
 * so the event lands on the day the admin actually picked.
 */
function localPartsToIso(dateStr, timeStr, defaultHour, defaultMin) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  let hour = defaultHour;
  let min = defaultMin;
  if (timeStr) {
    const [h, mi] = timeStr.split(':').map(Number);
    hour = h;
    min = mi;
  }
  return new Date(y, m - 1, d, hour, min).toISOString();
}

/** Split an ISO datetime into LOCAL date (YYYY-MM-DD) + time (HH:MM) parts. */
function isoToLocalParts(iso) {
  if (!iso) return { date: '', time: '' };
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
    time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
  };
}

function formatEventDate(event) {
  if (!event.starts_at) return '—';
  const start = formatDate(event.starts_at);
  if (!event.ends_at) return start;
  const end = formatDate(event.ends_at);
  return start === end ? start : `${start} – ${end}`;
}

export default function AdminEvents() {
  const toast = useToast();
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [galleryDirty, setGalleryDirty] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    eventsApi
      .list({ per_page: 100 })
      .then((res) => setState({ loading: false, error: null, items: res.items }))
      .catch((error) => setState({ loading: false, error, items: [] }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setGalleryDirty(false);
  }

  /** Mark the gallery dirty and apply a functional update from GalleryEditor. */
  function setGallery(updater) {
    setForm((f) => ({ ...f, gallery: updater(f.gallery) }));
    setGalleryDirty(true);
  }

  async function editEvent(event) {
    // Fetch the full record so the existing gallery loads even if the list row
    // omits the relation (mirrors AdminNews editNews / AdminActivities).
    let full = event;
    try {
      full = await eventsApi.get(event.slug || event.id);
    } catch {
      full = event;
    }
    const start = isoToLocalParts(full.starts_at);
    const end = isoToLocalParts(full.ends_at);
    setForm({
      id: full.id,
      title: full.title || '',
      startDate: start.date,
      startTime: start.time,
      endDate: end.date,
      endTime: end.time,
      location: full.location || '',
      capacity: full.capacity == null ? '' : String(full.capacity),
      description: full.description || '',
      isFeatured: !!full.is_featured,
      gallery: galleryItemsFromRecord(full),
      _status: full.status,
    });
    setGalleryDirty(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function buildPayload() {
    const startHour = form.startTime ? undefined : 9;
    const startMin = form.startTime ? undefined : 0;
    const starts_at = localPartsToIso(
      form.startDate,
      form.startTime,
      startHour ?? 9,
      startMin ?? 0
    );
    const ends_at = form.endDate
      ? localPartsToIso(form.endDate, form.endTime, form.endTime ? 0 : 17, 0)
      : null;

    return {
      title: form.title.trim(),
      description: form.description || null,
      location: form.location || null,
      starts_at,
      ends_at,
      capacity: form.capacity === '' ? null : Number(form.capacity),
    };
  }

  /** Run the approval state machine up to "published" from whatever the current status is. */
  async function transitionToPublished(id, currentStatus) {
    const order = ['draft', 'pending', 'approved', 'published'];
    let idx = order.indexOf(currentStatus || 'draft');
    if (idx === -1) idx = 0;
    if (currentStatus === 'rejected') {
      // rejected -> resubmit -> approve -> publish
      idx = 0;
    }
    const steps = [];
    if (idx < 1) steps.push('submit');
    if (idx < 2) steps.push('approve');
    if (idx < 3) steps.push('publish');
    for (const step of steps) {
      await eventsApi[step](id, 'Published via Admin Events manager.');
    }
  }

  async function handleSave(publish) {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!form.startDate) {
      toast.error('Start date is required.');
      return;
    }

    // Build the gallery payload up front so a missing media reference is caught
    // before we hit the API. Omitted entirely unless the gallery was touched.
    let galleryPayload;
    if (galleryDirty) {
      try {
        galleryPayload = buildGalleryPayload(form.gallery); // may be [] to clear
      } catch (err) {
        toast.error(err.message);
        return;
      }
    }

    setBusy(true);
    try {
      const payload = buildPayload();
      if (galleryDirty) payload.gallery = galleryPayload;
      let saved;
      if (form.id) {
        saved = await eventsApi.update(form.id, payload);
      } else {
        saved = await eventsApi.create(payload);
      }
      const id = saved?.id ?? form.id;
      const status = saved?.status ?? form._status ?? 'draft';

      if (publish && status !== 'published') {
        await transitionToPublished(id, status);
        toast.success('Event saved and published.');
      } else if (publish) {
        toast.success('Event saved (already published).');
      } else {
        toast.success(form.id ? 'Event updated.' : 'Draft saved.');
      }
      resetForm();
      load();
    } catch (err) {
      let handled = false;
      if (err.status === 422 && galleryDirty) {
        const { items, matched } = applyGalleryErrors(form.gallery, err.errors);
        if (matched) {
          setForm((f) => ({ ...f, gallery: items }));
          toast.error('Please fix the highlighted gallery items.');
          handled = true;
        }
      }
      if (!handled) {
        toast.error(err.message || 'Could not save event.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(event) {
    setBusy(true);
    try {
      await eventsApi.remove(event.id);
      toast.success('Event deleted.');
      setConfirmDelete(null);
      if (form.id === event.id) resetForm();
      load();
    } catch (err) {
      toast.error(err.message || 'Could not delete event.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublishRow(event) {
    setBusy(true);
    try {
      await transitionToPublished(event.id, event.status);
      toast.success('Event published.');
      load();
    } catch (err) {
      toast.error(err.message || 'Could not publish event.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <Seo title="Manage Events" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Events</h1>
        {form.id && (
          <button onClick={resetForm} className="btn-ghost text-sm" disabled={busy}>
            + New event
          </button>
        )}
      </div>

      {/* Create / edit panel */}
      <div className="card p-6">
        <h2 className="mb-4 font-display text-lg font-semibold">
          {form.id ? 'Edit event' : 'New event'}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Title *">
              <input
                className="input"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="Event title"
              />
            </Field>
          </div>

          <Field label="Start date *">
            <input
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) => update('startDate', e.target.value)}
            />
          </Field>
          <Field label="Start time" hint="Defaults to 9:00 AM if left blank.">
            <input
              type="time"
              className="input"
              value={form.startTime}
              onChange={(e) => update('startTime', e.target.value)}
            />
          </Field>

          <Field label="End date" hint="Optional — for multi-day events.">
            <input
              type="date"
              className="input"
              value={form.endDate}
              onChange={(e) => update('endDate', e.target.value)}
            />
          </Field>
          <Field label="End time" hint="Defaults to 5:00 PM if an end date is set.">
            <input
              type="time"
              className="input"
              value={form.endTime}
              onChange={(e) => update('endTime', e.target.value)}
            />
          </Field>

          <Field label="Location">
            <input
              className="input"
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              placeholder="Venue or address"
            />
          </Field>
          <Field label="Capacity">
            <input
              type="number"
              min="0"
              className="input"
              value={form.capacity}
              onChange={(e) => update('capacity', e.target.value)}
              placeholder="e.g. 50"
            />
          </Field>

          <div className="sm:col-span-2">
            <Field label="Description" hint="Format text, add links, insert images, and build tables.">
              <RichTextEditor
                id="event-description"
                value={form.description}
                onChange={(html) => update('description', html)}
                placeholder="What is this event about?"
              />
            </Field>
          </div>

          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(e) => update('isFeatured', e.target.checked)}
              />
              Featured event
            </label>
          </div>
        </div>

        {/* Gallery: add photos to show on the event page. */}
        <div className="mt-6">
          <GalleryEditor
            items={form.gallery}
            onChange={setGallery}
            disabled={busy}
            onUploadingChange={setGalleryUploading}
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={() => handleSave(true)}
            className="btn-primary"
            disabled={busy || galleryUploading}
          >
            {busy ? 'Working…' : 'Save & Publish'}
          </button>
          <button
            onClick={() => handleSave(false)}
            className="btn-outline"
            disabled={busy || galleryUploading}
          >
            Save as Draft
          </button>
          {form.id && (
            <button onClick={resetForm} className="btn-ghost" disabled={busy}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Events list */}
      {state.loading ? (
        <PageLoader />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : state.items.length === 0 ? (
        <EmptyState title="No events yet" message="Create your first event using the form above." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Location</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {state.items.map((e) => (
                <tr key={e.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{e.title}</td>
                  <td className="px-5 py-3 text-ink-muted">{formatEventDate(e)}</td>
                  <td className="px-5 py-3 text-ink-muted">{e.location || '—'}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <a
                        href={`/events/${e.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost px-3 py-1.5 text-xs"
                      >
                        View
                      </a>
                      <button
                        onClick={() => editEvent(e)}
                        className="btn-outline px-3 py-1.5 text-xs"
                      >
                        Edit
                      </button>
                      <Link
                        to={`/admin/events/${e.slug}/recaps`}
                        className="btn-ghost px-3 py-1.5 text-xs"
                      >
                        Recaps
                      </Link>
                      {e.status !== 'published' && (
                        <button
                          onClick={() => handlePublishRow(e)}
                          className="btn-primary px-3 py-1.5 text-xs"
                          disabled={busy}
                        >
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(e)}
                        className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={() => setConfirmDelete(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Delete event?</h3>
            <p className="mt-2 text-sm text-ink-muted">
              “{confirmDelete.title}” will be permanently removed. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost" disabled={busy}>
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="btn-danger"
                disabled={busy}
              >
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
